import { Registry, type Identified } from './Registry';

/**
 * NPC tanımları: meslek, günlük rutin ve diyalog havuzu.
 * Diyaloglar saat/hava/meslek/görev durumuna göre veriden seçilir.
 */

export type Profession =
  | 'trader'
  | 'farmer'
  | 'blacksmith'
  | 'carpenter'
  | 'cook'
  | 'healer'
  | 'fisher'
  | 'guard'
  | 'wanderer';

export type RoutineActivity = 'sleep' | 'home' | 'work' | 'wander' | 'guard' | 'market';

export interface RoutineSlot {
  /** Başlangıç saati (0..24). */
  from: number;
  activity: RoutineActivity;
}

export interface DialogueSet {
  greeting: string[];
  day?: string[];
  night?: string[];
  rain?: string[];
  questAvailable?: string[];
  questDone?: string[];
  farewell?: string[];
}

export interface NpcDef extends Identified {
  name: string;
  profession: Profession;
  icon: string;
  /** Rastgele isim havuzu (procedural köylerde kullanılır). */
  namePool?: string[];
  routine: RoutineSlot[];
  dialogue: DialogueSet;
  /** Dükkân açıyor mu? */
  shop?: { stockTable: string; buyMultiplier: number; sellMultiplier: number; openHour: number; closeHour: number };
  questGiver?: boolean;
  /** Köyde kaç tane bulunabilir. */
  maxPerVillage?: number;
}

export const npcs = new Registry<NpcDef>('npcs');

const COMMON_ROUTINE: RoutineSlot[] = [
  { from: 0, activity: 'sleep' },
  { from: 6, activity: 'home' },
  { from: 7, activity: 'work' },
  { from: 12, activity: 'market' },
  { from: 13, activity: 'work' },
  { from: 19, activity: 'home' },
  { from: 22, activity: 'sleep' },
];

npcs.register({
  id: 'bedo',
  name: 'BEDO',
  profession: 'trader',
  icon: '🧔',
  routine: [
    { from: 0, activity: 'sleep' },
    { from: 6, activity: 'home' },
    { from: 8, activity: 'market' },
    { from: 20, activity: 'home' },
    { from: 22, activity: 'sleep' },
  ],
  shop: { stockTable: 'shop_bedo', buyMultiplier: 1, sellMultiplier: 0.55, openHour: 8, closeHour: 20 },
  questGiver: true,
  maxPerVillage: 1,
  dialogue: {
    greeting: [
      'Altın dişimle yeri göğü aydınlatıyorum patron!',
      'Hoş geldin. Kesende Kogox varsa dükkân senin.',
      'Bugün stok taze, yarın kim bilir.',
    ],
    day: ['Gündüz iş, gece uyku. Dükkân açık patron.'],
    night: ['Dükkân kapalı patron, sabah gel. Gece bu adada dolaşmak akıllıca değil.'],
    rain: ['Bu yağmurda balık tutan kazanır, unutma.'],
    questAvailable: ['Sana üç iş var patron, hangisini alırsın?'],
    questDone: ['Bugünlük işler tamam. Yarın yine gel.'],
    farewell: ['Kogox’un bol olsun.'],
  },
});

npcs.register(
  {
    id: 'farmer',
    name: 'Çiftçi',
    profession: 'farmer',
    icon: '👨‍🌾',
    namePool: ['Hasan', 'Ayşe', 'Mert', 'Zeynep', 'Kadir'],
    routine: COMMON_ROUTINE,
    maxPerVillage: 3,
    dialogue: {
      greeting: ['Tarla kendi kendine sulanmıyor.', 'Tohumun varsa toprağım var.'],
      night: ['Bu saatte tarlada ne işim olsun, eve gidiyorum.'],
      rain: ['Yağmur bereket, buğday sevinir.'],
    },
  },
  {
    id: 'blacksmith',
    name: 'Demirci',
    profession: 'blacksmith',
    icon: '🧑‍🏭',
    namePool: ['Demir Ali', 'Örs Osman', 'Çelik Nur'],
    routine: COMMON_ROUTINE,
    maxPerVillage: 1,
    shop: { stockTable: 'shop_blacksmith', buyMultiplier: 1.15, sellMultiplier: 0.6, openHour: 7, closeHour: 19 },
    dialogue: {
      greeting: ['Demir sıcakken dövülür.', 'Kazman kırıldıysa doğru yerdesin.'],
      night: ['Ocak söndü, sabah gel.'],
    },
  },
  {
    id: 'carpenter',
    name: 'Marangoz',
    profession: 'carpenter',
    icon: '🪚',
    namePool: ['Tahta Timur', 'Rende Rıza'],
    routine: COMMON_ROUTINE,
    maxPerVillage: 1,
    dialogue: { greeting: ['Ev mi kuruyorsun? Kirişleri sağlam seç.'] },
  },
  {
    id: 'cook',
    name: 'Aşçı',
    profession: 'cook',
    icon: '🧑‍🍳',
    namePool: ['Tencere Tuncay', 'Baharat Bahar'],
    routine: COMMON_ROUTINE,
    maxPerVillage: 1,
    dialogue: { greeting: ['Aç mısın? Çorbam hazır.'], night: ['Mutfak kapandı.'] },
  },
  {
    id: 'healer',
    name: 'Şifacı',
    profession: 'healer',
    icon: '🧙',
    namePool: ['Ot Nine', 'Şifa Sedat'],
    routine: COMMON_ROUTINE,
    maxPerVillage: 1,
    shop: { stockTable: 'shop_healer', buyMultiplier: 1.2, sellMultiplier: 0.5, openHour: 8, closeHour: 18 },
    dialogue: {
      greeting: ['Zehirlendiysen doğru kapıyı çaldın.'],
      night: ['Gece iksir karıştırılmaz, el titrer.'],
    },
  },
  {
    id: 'fisher',
    name: 'Balıkçı',
    profession: 'fisher',
    icon: '🎣',
    namePool: ['Olta Orhan', 'Ağ Ayhan'],
    routine: [
      { from: 0, activity: 'sleep' },
      { from: 5, activity: 'work' },
      { from: 14, activity: 'market' },
      { from: 16, activity: 'work' },
      { from: 20, activity: 'home' },
      { from: 23, activity: 'sleep' },
    ],
    maxPerVillage: 2,
    dialogue: {
      greeting: ['Sabah erken, akşam geç. Balık öyle sever.'],
      night: ['Gece suları başkadır. Nadir olan geceyi bekler.'],
      rain: ['Yağmurda oltayı at, pişman olmazsın.'],
    },
  },
  {
    id: 'guard',
    name: 'Muhafız',
    profession: 'guard',
    icon: '💪',
    namePool: ['Bekçi Barış', 'Kalkan Kaya'],
    routine: [
      { from: 0, activity: 'guard' },
      { from: 8, activity: 'guard' },
      { from: 20, activity: 'guard' },
    ],
    maxPerVillage: 4,
    dialogue: {
      greeting: ['Köyde sorun istemiyoruz.'],
      night: ['Gece dışarı çıkma. Kurtlar aç.'],
    },
  },
  {
    id: 'wanderer',
    name: 'Gezgin',
    profession: 'wanderer',
    icon: '🎒',
    namePool: ['Yolcu Yusuf', 'Sırt Çantası Sinan'],
    routine: [
      { from: 0, activity: 'sleep' },
      { from: 7, activity: 'wander' },
      { from: 21, activity: 'sleep' },
    ],
    maxPerVillage: 1,
    dialogue: {
      greeting: ['Kuzeyde mor bir sis gördüm. Yaklaşmadım.', 'Nehrin altında bir kapı var derler.'],
      night: ['Ateş başında oturmak gibisi yok.'],
    },
  },
);

/** Günlük görev şablonları (BEDO her oyun günü 3 tane sunar). */
export interface QuestTemplate extends Identified {
  kind: 'gather' | 'hunt' | 'fish' | 'mine' | 'explore' | 'craft';
  text: string;
  /** Hedef item/canlı id'si; birden fazlaysa rastgele seçilir. */
  targets: string[];
  amountMin: number;
  amountMax: number;
  /** Ödül: Kogox + opsiyonel item. */
  kogox: [number, number];
  rewards?: Array<{ item: string; min: number; max: number; chance: number }>;
}

export const questTemplates = new Registry<QuestTemplate>('quests');

questTemplates.register(
  {
    id: 'gather_wood',
    kind: 'gather',
    text: 'Bana {amount} odun getir.',
    targets: ['wood'],
    amountMin: 10,
    amountMax: 30,
    kogox: [25, 60],
    rewards: [{ item: 'rope', min: 1, max: 3, chance: 0.4 }],
  },
  {
    id: 'gather_stone',
    kind: 'gather',
    text: '{amount} taş lazım, ocak boş kaldı.',
    targets: ['stone'],
    amountMin: 10,
    amountMax: 30,
    kogox: [25, 60],
  },
  {
    id: 'mine_iron',
    kind: 'mine',
    text: '{amount} demir çıkar, demirci bekliyor.',
    targets: ['iron'],
    amountMin: 3,
    amountMax: 10,
    kogox: [60, 140],
    rewards: [{ item: 'coal', min: 2, max: 6, chance: 0.5 }],
  },
  {
    id: 'mine_precious',
    kind: 'mine',
    text: '{amount} değerli maden bul.',
    targets: ['gold', 'silver', 'emerald'],
    amountMin: 2,
    amountMax: 6,
    kogox: [120, 280],
  },
  {
    id: 'fish_any',
    kind: 'fish',
    text: '{amount} balık tut, mutfak boş.',
    targets: ['fish_minnow', 'fish_carp', 'fish_bass'],
    amountMin: 3,
    amountMax: 8,
    kogox: [40, 90],
    rewards: [{ item: 'bread', min: 1, max: 2, chance: 0.5 }],
  },
  {
    id: 'hunt_beast',
    kind: 'hunt',
    text: '{amount} yırtıcı avla, köy rahat etsin.',
    targets: ['wolf', 'boar'],
    amountMin: 2,
    amountMax: 5,
    kogox: [70, 160],
  },
  {
    id: 'craft_tool',
    kind: 'craft',
    text: '{amount} alet üret, gösteriş yapayım.',
    targets: ['stone_pickaxe', 'iron_pickaxe', 'stone_axe'],
    amountMin: 1,
    amountMax: 2,
    kogox: [50, 110],
  },
  {
    id: 'explore_far',
    kind: 'explore',
    text: 'Köyden uzaklaş, {amount} yeni bölge keşfet.',
    targets: ['chunk'],
    amountMin: 4,
    amountMax: 12,
    kogox: [60, 150],
  },
);
